import React, { useEffect, useRef, useState } from 'react';
import { CAlert, CButton, CFormSelect, CSpinner } from '@coreui/react-pro';
import { useNavigate } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useFleet } from '../../contexts/FleetContext';
import { FleetDelivery } from '../../types/fleet';
import { showToastTR } from '../../Utils/BaseApp';
import notificationSound from '../../assets/sounds/notification.mp3';

interface FleetHeaderProps {
    title: string;
    description: string;
    actions?: React.ReactNode;
    // Las páginas que ya traen alertas (Flota en vivo) las pasan para no consultarlas dos veces
    alerts?: FleetDelivery[];
}

const ALERTS_REFRESH_MS = 30000;

/**
 * Encabezado de "Mi flota": título, selector de empresa (si hay varias) y aviso de pedidos sin
 * domiciliario. Suena cuando aparece un pedido nuevo sin domiciliario.
 */
export const FleetHeader: React.FC<FleetHeaderProps> = ({ title, description, actions, alerts: alertsFromPage }) => {
    const navigate = useNavigate();
    const { companies, company, setCompanyId } = useFleet();
    const [alerts, setAlerts] = useState<FleetDelivery[]>([]);
    const seenAlertIds = useRef<Set<number> | null>(null);

    useEffect(() => {
        seenAlertIds.current = null;
    }, [company?.id]);

    useEffect(() => {
        if (alertsFromPage || !company) return;
        const load = () =>
            DomicilappService.getFleetAlerts(company.id)
                .then((response) => setAlerts(response?.data ?? []))
                .catch(() => undefined);
        load();
        const interval = setInterval(load, ALERTS_REFRESH_MS);
        return () => clearInterval(interval);
    }, [company, alertsFromPage]);

    const currentAlerts = alertsFromPage ?? alerts;

    // Aviso sonoro solo para pedidos sin domiciliario que no se habían visto
    useEffect(() => {
        const ids = new Set(currentAlerts.map((a) => a.id));
        if (seenAlertIds.current) {
            const fresh = currentAlerts.filter((a) => !seenAlertIds.current!.has(a.id));
            if (fresh.length > 0) {
                new Audio(notificationSound).play().catch(() => undefined);
                showToastTR(`Pedido #${fresh[0].id} sin domiciliario`, 5000, 'info');
            }
        }
        seenAlertIds.current = ids;
    }, [currentAlerts]);

    return (
        <>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                    <h3 className="fw-bold text-body mb-1">{title}</h3>
                    <p className="text-body-secondary small mb-0">
                        {description}
                        {company ? ` · ${company.name}${company.municipality_name ? ` (${company.municipality_name})` : ''}` : ''}
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {companies.length > 1 && company && (
                        <CFormSelect size="sm" value={company.id} onChange={(e) => setCompanyId(Number(e.target.value))} style={{ minWidth: 200 }}>
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </CFormSelect>
                    )}
                    {actions}
                </div>
            </div>

            {currentAlerts.length > 0 && (
                <CAlert color="warning" className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
                    <span>
                        <strong>{currentAlerts.length} {currentAlerts.length === 1 ? 'pedido lleva' : 'pedidos llevan'} varios minutos sin domiciliario.</strong>{' '}
                        La búsqueda automática sigue; puedes asignarlos tú.
                    </span>
                    <CButton size="sm" color="dark" onClick={() => navigate('/fleet/deliveries?tab=unassigned')}>
                        Ver y asignar
                    </CButton>
                </CAlert>
            )}
        </>
    );
};

// Para las páginas: muestra estados de carga / sin empresa antes del contenido
export const FleetGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { companies, companiesLoaded, company } = useFleet();
    if (!companiesLoaded) {
        return (
            <div className="text-center py-5">
                <CSpinner size="sm" className="me-2" />
                <span className="text-body-secondary">Cargando tu empresa de domicilios...</span>
            </div>
        );
    }
    if (companies.length === 0 || !company) {
        return <CAlert color="secondary">Tu usuario no está asociado a ninguna empresa de domicilios.</CAlert>;
    }
    return <>{children}</>;
};
